"use client";
import {
  ConnectedWallet,
  PollTxOptions,
  UnsignedTx,
  WalletController,
  WalletName,
  WalletType,
} from "@goblinhunt/cosmes/wallet";
import React, { useEffect, useState } from "react";
import { createContext, ReactNode, useContext } from "react";

import { SUPPORTED_WALLETS, WalletSupport } from "../config/wallet.config";
import { useAuthClient } from "@hooks/BaaangggHook";
import { CHAINS, CHAINS_SELECTED, CHAINS_USAGE, MULTIPLIER, WALLET_ID_KEY, WALLET_TYPE_KEY } from "@constant/constant";

export type Tax = {
  denom: string;
  amount: number;
};

type Prop = {
  id?: string;
  address?: string;
  chain?: CHAINS;
  setChain: (chain: CHAINS) => void;
  connect: (wallet: WalletSupport) => Promise<void>;
  disconnect: () => void;
  estimateFee: (tx: UnsignedTx) => Promise<Fee>;
  setChainStorage: (chain: string) => void;
  post: (tx: UnsignedTx, taxes?: Tax[]) => Promise<string>;
  pollTx?: (txHash: string) => Promise<any>;
  wallet?: ConnectedWallet;
};

const WalletContext = createContext<Prop>({
  chain: CHAINS.mainnet,
  setChain: () => {},
  connect: async () => {},
  disconnect: () => {},
  estimateFee: async () => {
    throw new Error("Wallet belum siap untuk estimateFee");
  },
  setChainStorage: () => {},
  post: async () => "",
});

export const useWallet = () => useContext(WalletContext);
export function WalletProvider({ children }: { children: ReactNode }) {
  const [chain, setChain] = useState<CHAINS | undefined>(undefined);
  const [wallet, setWallet] = useState<ConnectedWallet>();
  const [controller, setController] = useState<WalletController>();

  const store = (id: string, type: string) => {
    localStorage.setItem(WALLET_ID_KEY, id);
    localStorage.setItem(WALLET_TYPE_KEY, type);
  };

  const clear = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(WALLET_ID_KEY);
    localStorage.removeItem(WALLET_TYPE_KEY);
  };

  const setChainStorage = (chain: string) => {
    localStorage.setItem(CHAINS_SELECTED, chain);
    setChain(chain as CHAINS);
  };

  const getChainStorage = () => {
    const chain: string | null = localStorage.getItem(CHAINS_SELECTED);
    if (chain != null) {
      setChain(chain as CHAINS);
    } else {
      setChainStorage(CHAINS_USAGE);
      setChain(CHAINS_USAGE as CHAINS);
    }
  };

  const loadWalletFromStorage = async () => {
    const id = localStorage.getItem(WALLET_ID_KEY);
    const type = localStorage.getItem(WALLET_TYPE_KEY);

    if (id && type) {
      const supportWallet = new WalletSupport(
        id as WalletName,
        type as WalletType
      );
      setTimeout(async () => await connect(supportWallet), 500);
    }
  };

  const connect = async (walletSupport: WalletSupport) => {
    try {
      const wallet = await walletSupport.connect(chain as CHAINS);
      console.log("Connected to chain:", chain);
      console.log(wallet);
      setWallet(wallet);
      store(wallet!.id, wallet!.type);
    } catch (e) {
      console.error("Connection error:", e);
      alert(`Connection error: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const disconnect = () => {
    clear();
    try {
      controller?.disconnect?.([chain as CHAINS]);
    } catch {}
    setWallet(undefined);
    setController(undefined);
  };

  const estimateFee = async (tx: UnsignedTx): Promise<Fee> => {
    let fee = await wallet!.estimateFee(tx, MULTIPLIER);
    return fee;
  };

  const post = async (tx: UnsignedTx, taxes?: Tax[]) => {
    let fee = await wallet!.estimateFee(tx, MULTIPLIER);

    if (taxes)
      fee.amount = fee.amount.map((coin) => {
        const tax = taxes.find((e) => e.denom == coin.denom);
        if (tax) coin.amount = (Number(coin.amount) + tax.amount).toString();

        return coin;
      });

    return await wallet!.broadcastTx(tx, fee);
  };

  useEffect(() => {
    for (const controller of Object.values(SUPPORTED_WALLETS).map(
      (e) => e.controller
    )) {
      controller.onDisconnect((_) => disconnect());
    }
    getChainStorage();
  });

  useEffect(() => {
    if (chain) loadWalletFromStorage();
  }, [chain]);

  return (
    <WalletContext.Provider
      value={{
        setChain,
        chain,
        pollTx: wallet?.pollTx
          ? async (txHash) =>
              wallet.pollTx(txHash, { intervalSeconds: 1, maxAttempts: 20 })
          : undefined,
        post,
        connect,
        disconnect,
        estimateFee,
        setChainStorage,
        wallet,
        address: wallet?.address,
        id: wallet?.id,
      }}
    >
      {children} {/* ✅ Correct way to use children */}
    </WalletContext.Provider>
  );
}

type Coin = {
  denom: string;
  amount: string;
};

type ExecuteMessage = {
  sender: string;
  contract: string;
  msg: any;
  funds?: Coin[];
};

type Fee = {
  gasLimit: bigint;
  amount: Coin[];
};
